import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
  RouteReuseStrategy,
  withComponentInputBinding,
} from '@angular/router';
import { DatePipe } from '@angular/common';
import { SocketIoModule } from 'ngx-socket-io';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { WebIntent } from '@awesome-cordova-plugins/web-intent/ngx';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { GoogleLoginProvider, SOCIAL_AUTH_CONFIG } from '@abacritt/angularx-social-login';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';

import { routes } from '@app/app.routes';
import { environment } from '@env/environment';
import { UserService } from '@app/services/user.service';
import { ThemeService } from '@app/services/theme.service';
import { StatusService } from '@app/services/status.service';
import { LabelsService } from '@app/services/labels.service';
import { rpcInterceptor } from '@app/interceptors/rpc.interceptor';
import { authInterceptor } from '@app/interceptors/auth.interceptor';
import { TodayAwareDatePipe } from '@app/pipes/today-aware-date.pipe';
import { errorInterceptor } from '@app/interceptors/error.interceptor';
import { ConferencesService } from '@app/services/conferences.service';
import { CallerBlocksService } from '@app/services/caller-blocks.service';
import { LabelMetadataService } from '@app/services/label-metadata.service';
import { ActiveEndpointService } from '@app/services/active-endpoint.service';
import { OfflineDialCodesService } from '@app/services/offline-dial-codes.service';

export function initializeApp(
  userSrv: UserService,
  themeSrv: ThemeService,
  activeEndpointSrv: ActiveEndpointService,
  statusSrv: StatusService,
  offlineDialCodeSrv: OfflineDialCodesService,
  conferencesSrv: ConferencesService,
  labelsSrv: LabelsService,
  labelMetadataSrv: LabelMetadataService,
  callerBlocksSrv: CallerBlocksService,
) {
  return (): Promise<
    [
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
    ]
  > =>
    Promise.allSettled([
      userSrv.init(),
      themeSrv.init(),
      activeEndpointSrv.assignEndpoint(),
      statusSrv.assignStatus(),
      offlineDialCodeSrv.assignGsmNumbers(),
      conferencesSrv.assignLastConference(),
      labelsSrv.assignLabels(),
      labelMetadataSrv.assignMetadataColumns(),
      callerBlocksSrv.assignCallerBlockReasons(),
    ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({ useSetInputAPI: true, innerHTMLTemplatesEnabled: true, backButtonText: '' }),
    provideRouter(routes, withComponentInputBinding(), withPreloading(PreloadAllModules)),
    importProvidersFrom(
      SocketIoModule.forRoot({
        url: environment.socketIoUrl,
        options: {
          path: environment.socketIoPath,
        },
      }),
    ),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(UserService),
        inject(ThemeService),
        inject(ActiveEndpointService),
        inject(StatusService),
        inject(OfflineDialCodesService),
        inject(ConferencesService),
        inject(LabelsService),
        inject(LabelMetadataService),
        inject(CallerBlocksService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const msalInstance = inject(MSAL_INSTANCE) as IPublicClientApplication;
      return msalInstance.initialize();
    }),
    provideHttpClient(withInterceptors([authInterceptor, rpcInterceptor, errorInterceptor])),
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientId),
          },
        ],
        onError: (err: unknown) => console.error(err),
      },
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        return new PublicClientApplication(environment.msalConfig);
      },
    },
    TodayAwareDatePipe,
    MsalService,
    DatePipe,
    WebIntent,
  ],
};
